import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-styled-flexboxgrid';
import styled from 'styled-components';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { TextField, Icon, NewButton, DropdownMenuToggle, Button } from '../../../../../components';
import { AccessManagementDropdown } from '../../molecules';

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: 20px;
`;

const StyledInput = styled(TextField)`
  width: 100%;
  min-width: 200px;
  margin-bottom: 0;
`;

const StyledDropdownMenuToggle = styled(DropdownMenuToggle)`
  width: 100%;

  &:hover {
    background-color: transparent !important;
  }
`;

const StyledAccessManagementDropdown = styled(AccessManagementDropdown)`
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const StyledRoleButton = styled(Button)`
  border: 1px solid var(--border-muted);
  background: var(--white);
  border-radius: 8px;
  padding: 0;
  min-width: 140px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;

  /* Target the Label component inside Button */
  > label {
    width: 100%;
  }
`;

const InviteUserFormContainer = styled.div`
  padding: 16px 24px 24px 24px;
`;

const InviteUserForm = ({ onSubmit, roles, usedEmailsList }) => {
  const defaultRole = roles[0];

  const schema = yup.object().shape({
    user: yup.array().of(
      yup.object().shape({
        email: yup
          .string()
          .required('Enter email address')
          .email('Please enter a valid email address')
          .test(
            'unique email',
            'This email is already in use',
            (value) => [...usedEmailsList].includes(value) === false
          ),
        roleId: yup.string().required('Select role').default(defaultRole.id)
      })
    )
  });

  const [selectedRoles, setSelectedRoles] = useState([defaultRole]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      user: [{ email: '', roleId: defaultRole.id }]
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit'
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'user'
  });

  const handleSelect = (index, newRole) => {
    setSelectedRoles((prevRoles) => [
      ...prevRoles.slice(0, index),
      newRole,
      ...prevRoles.slice(index + 1)
    ]);
    setValue(`user.${index}.roleId`, newRole.id);
  };

  const handleRemove = (index) => {
    remove(index);
    setSelectedRoles((prevRoles) => [...prevRoles.slice(0, index), ...prevRoles.slice(index + 1)]);
  };

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <InviteUserFormContainer>
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        {fields.map((field, i) => (
          <Row key={i}>
            <Col xs={12}>
              <StyledWrapper key={field.id}>
                <StyledInput
                  type='email'
                  placeholder='Email'
                  {...register(`user.${i}.email`)}
                  error={errors?.user?.[i]?.email?.message}
                />
                <StyledAccessManagementDropdown
                  roles={roles}
                  index={i}
                  currentRole={selectedRoles[i]}
                  onSelect={handleSelect}
                  usePortal
                  menuStyle={{ zIndex: 'calc(var(--z-modal) + 1)' }}
                  toggleComponent={
                    <StyledRoleButton tertiary>
                      <StyledDropdownMenuToggle text={selectedRoles[i]?.name} />
                    </StyledRoleButton>
                  }
                />
                <NewButton onClick={() => handleRemove(i)}>
                  <Icon name='trash' displaySize={24} />
                </NewButton>
              </StyledWrapper>
            </Col>
          </Row>
        ))}
        <Row style={{ marginBottom: 24 }}>
          <Col xs={12}>
            <NewButton
              block
              onClick={() => {
                setSelectedRoles((prevRoles) => [...prevRoles, defaultRole]);
                append({ email: '', role_id: defaultRole.id });
              }}
            >
              <Icon name='plus' displaySize={20} />
              Add another
            </NewButton>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <StyledWrapper>
              <NewButton naked>
                <Icon name='link' displaySize={20} color='var(--content-body)' />
                Copy invite link
              </NewButton>
              <NewButton primary isSubmitting={isSubmitting} type='submit'>
                Send invitation
              </NewButton>
            </StyledWrapper>
          </Col>
        </Row>
      </form>
    </InviteUserFormContainer>
  );
};

InviteUserForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string
    })
  ).isRequired,
  usedEmailsList: PropTypes.arrayOf(PropTypes.string).isRequired
};

export { InviteUserForm };
